/* eslint-disable jsx-a11y/label-has-associated-control */
import { ButtonBase, Grid, makeStyles, Typography } from '@material-ui/core';
import React from 'react';

import CheckIcon from '@material-ui/icons/Check';
import { colors, SonarPulse } from 'design-system';
import { compressImage } from 'src/functions/compressImage';
import isCaptureSupported from 'src/functions/isCaptureSupported';

export enum SonarStates {
  Active,
  Completed,
  Disabled,
}

type Props = {
  id?: string;
  sonarState?: SonarStates;
  absolutePosition: { left: number; top: number };
  displayText: string;
  onImageRequest: () => void;
  section: string;
  onPhotoCaptured: (identifier: string, base64image: string) => void;
};

const REGULAR_SIZE = 24;
const ACTIVE_SIZE = 68;

const useStyles = makeStyles({
  wrapperGrid: {
    display: 'inline-block',
    position: 'absolute',
    width: 'auto',
    transform: 'translateX(-50%) translateY(-50%)',
  },
  disabledSonar: {
    width: REGULAR_SIZE,
    height: REGULAR_SIZE,
    border: `4px solid ${colors.silver}`,
    borderRadius: REGULAR_SIZE / 2,
    display: 'inline-block',
  },
  completedSonar: {
    width: REGULAR_SIZE,
    height: REGULAR_SIZE,
    borderRadius: REGULAR_SIZE / 2,
    background: colors.bright_turquoise,
    color: colors.white,
  },
  activeSonar: {
    width: ACTIVE_SIZE,
    height: ACTIVE_SIZE,
  },
});

const TakePictureSonar = ({
  absolutePosition,
  displayText,
  onImageRequest,
  sonarState = SonarStates.Active,
  id,
  section,
  onPhotoCaptured,
}: Props): JSX.Element => {
  const classes = useStyles();
  const captureSupport = isCaptureSupported();
  let sonar: React.ReactNode;
  switch (sonarState) {
    case SonarStates.Active:
      sonar = (
        <>
          {captureSupport && (
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id={`${section}-camera`}
              data-testid="mobile-input"
              type="file"
              capture="environment"
              onChange={async (e) => {
                if (!e.target.files) return;
                const compressedImage = await compressImage(e.target.files[0]);
                onPhotoCaptured(section, compressedImage);
              }}
            />
          )}
          <label htmlFor={`${section}-camera`}>
            <ButtonBase
              id={id}
              data-testid="sonar-button"
              className={classes.activeSonar}
              onClick={onImageRequest}
              disableRipple
              style={{ pointerEvents: captureSupport ? 'none' : 'auto' }}
            >
              <SonarPulse maxWidth={ACTIVE_SIZE} />
            </ButtonBase>
          </label>
        </>
      );
      break;
    case SonarStates.Completed:
      sonar = (
        <Grid
          data-testid="completed-sonar"
          container
          alignItems="center"
          justifyContent="center"
          className={classes.completedSonar}
        >
          <CheckIcon style={{ fontSize: 16 }} />
        </Grid>
      );
      break;
    default:
      sonar = (
        <div data-testid="disabled-sonar" className={classes.disabledSonar} />
      );
  }

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="center"
      className={classes.wrapperGrid}
      style={{
        left: absolutePosition.left,
        top: absolutePosition.top,
      }}
    >
      {displayText === 'Interior(VIN)' ? (
        <Grid>
          <Grid item xs={12} container justifyContent="center">
            <Typography align="center" variant="caption">
              Interior
            </Typography>
          </Grid>
          <Grid item xs={12} container justifyContent="center">
            <Typography align="center" variant="caption">
              (VIN)
            </Typography>
          </Grid>
          <Grid item xs={12} container justifyContent="center">
            {sonar}
          </Grid>
        </Grid>
      ) : (
        <Grid>
          <Grid item xs={12} container justifyContent="center">
            {sonar}
          </Grid>
          <Grid item xs={12} container justifyContent="center">
            <Typography align="center" variant="caption">
              {displayText}
            </Typography>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default TakePictureSonar;
